import { FC, useEffect, useState } from 'react'
import { 
  Box, Typography, Grid2, Paper, alpha, Avatar, Button, 
  Card, CardActionArea, CardContent, CircularProgress, Divider 
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { 
  MdPeople, MdLocalShipping, MdHistory, MdSettings, 
  MdBrush, MdNavigateNext, MdOutlineDashboard, MdLabel,
  MdOutlinePrecisionManufacturing, MdVerifiedUser
} from 'react-icons/md'
import { useAuth } from '../../../Hooks/Auth'
import api from '../../../Services/api'

// ─── Componentes Internos ───────────────────────────────────────────────────

const IndicatorCard: FC<{ title: string; value: string | number; icon: React.ReactNode; color: string; loading?: boolean }> = ({ 
  title, value, icon, color, loading 
}) => {
  const theme = useTheme()
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.12)}`,
          borderColor: color
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: alpha(color, 0.1), 
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        {loading ? (
          <CircularProgress size={20} sx={{ color }} />
        ) : (
          <Typography variant="h4" fontWeight={900} color="text.primary">
            {value}
          </Typography>
        )}
      </Box>
      <Box sx={{ 
        position: 'absolute', 
        right: -10, 
        bottom: -10, 
        opacity: 0.05, 
        transform: 'rotate(-15deg)',
        fontSize: '4rem',
        color: color
      }}>
        {icon}
      </Box>
    </Paper>
  )
}

const ShortcutCard: FC<{ 
  title: string; 
  subtitle: string; 
  icon: React.ReactNode; 
  path: string; 
  color: string;
  badge?: string | number;
  badgeColor?: string;
  disabled?: boolean;
}> = ({ 
  title, subtitle, icon, path, color, badge, badgeColor, disabled 
}) => {
  const navigate = useNavigate()
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%', 
        borderRadius: 3,
        opacity: disabled ? 0.7 : 1,
        transition: 'all 0.2s ease',
        '&:hover': { 
          borderColor: disabled ? 'divider' : color, 
          bgcolor: disabled ? 'transparent' : alpha(color, 0.02),
          transform: disabled ? 'none' : 'translateY(-2px)'
        }
      }}
    >
      <CardActionArea 
        onClick={() => !disabled && navigate(path)} 
        disabled={disabled}
        sx={{ height: '100%', p: 1 }}
      >
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative' }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: '12px', 
            bgcolor: alpha(color, 0.1), 
            color: color,
            display: 'flex'
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.2 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          </Box>
          
          {badge !== undefined && (
            <Box sx={{ 
              px: 1.2, 
              py: 0.4, 
              borderRadius: 1.5, 
              bgcolor: badgeColor || color, 
              color: 'white',
              fontSize: '0.65rem',
              fontWeight: 900,
              boxShadow: `0 4px 8px ${alpha(badgeColor || color, 0.2)}`
            }}>
              {badge}
            </Box>
          )}

          {!disabled && <MdNavigateNext size={20} color={alpha('#000', 0.2)} />}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

// ─── Componente Principal ────────────────────────────────────────────────────

const HCPDashboard: FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [stats, setStats] = useState({
    collaborators: 0,
    freteHeroProducts: 0,
    pendingPrazos: 0,
    arteFinalCount: 0,
    montagemCount: 0,
    loading: true
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collabRes, productsRes, pendentesRes, kanbanRes] = await Promise.all([
          api.get('/collaborators').then(r => r.data).catch(() => []),
          api.get('/frete-hero/produtos').then(r => r.data).catch(() => []),
          api.get('/frete-hero/pendentes').then(r => r.data).catch(() => []),
          api.get('/customization-flow/kanban').then(r => r.data).catch(() => null)
        ])

        // Calcular total arte final (não inclui Aprovado Final se quisermos apenas andamento)
        let arteCount = 0
        if (kanbanRes && kanbanRes.columns) {
          arteCount = kanbanRes.columns
            .filter((c: any) => c.key !== 'aprovado_final')
            .reduce((acc: number, col: any) => acc + (col.count || 0), 0)
        }

        // Buscar especificamente Aprovados sem OS para Montagem
        const montagemRes = await api.get('/customization-flow/kanban/column', {
          params: { status: 'aprovado_final', tem_os: false, page: 1, page_size: 1 }
        }).then(r => r.data).catch(() => null)

        setStats({
          collaborators: Array.isArray(collabRes) ? collabRes.length : 0,
          freteHeroProducts: Array.isArray(productsRes) ? productsRes.length : 0,
          pendingPrazos: Array.isArray(pendentesRes) ? pendentesRes.length : 0,
          arteFinalCount: arteCount,
          montagemCount: montagemRes?.count || 0,
          loading: false
        })
      } catch (e) {
        console.error('Error fetching dashboard stats', e)
        setStats(prev => ({ ...prev, loading: false }))
      }
    }

    fetchData()
  }, [])

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: '0 auto' }}>
      
      {/* Banner de Boas-vindas */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 3, md: 5 }, 
          borderRadius: 4, 
          bgcolor: 'primary.main',
          backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="overline" sx={{ opacity: 0.8, fontWeight: 700, letterSpacing: 2 }}>
            Portal do Administrador
          </Typography>
          <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: -1.5 }}>
            {/* Formatação do nome: remove @..., remove pontos, e capitaliza */}
            Olá, {user?.username ? (user.username.split('@')[0].split('.')[0].charAt(0).toUpperCase() + user.username.split('@')[0].split('.')[0].slice(1)) : 'Gestor'}! 👋
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 600, mt: 1 }}>
            Bem-vindo ao HCP — Hub de Controle da Produção. Como está a produtividade hoje?
          </Typography>
        </Box>

        {/* Círculos decorativos */}
        <Box sx={{ 
          position: 'absolute', right: -50, top: -50, width: 250, height: 250, 
          borderRadius: '50%', border: '40px solid rgba(255,255,255,0.05)', zIndex: 1 
        }} />
        <Box sx={{ 
          position: 'absolute', right: 50, bottom: -80, width: 180, height: 180, 
          borderRadius: '50%', border: '30px solid rgba(255,255,255,0.03)', zIndex: 1 
        }} />
      </Paper>

      {/* Grid de Indicadores */}
      <Grid2 container spacing={3} sx={{ mb: 6 }}>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <IndicatorCard 
            title="Produção no Ar" 
            value={stats.freteHeroProducts} 
            icon={<MdLocalShipping size={24} />} 
            color={theme.palette.primary.main} 
            loading={stats.loading}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <IndicatorCard 
            title="Equipe HCP" 
            value={stats.collaborators} 
            icon={<MdPeople size={24} />} 
            color="#1976d2" 
            loading={stats.loading}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <IndicatorCard 
            title="Prazos Pendentes" 
            value={stats.pendingPrazos} 
            icon={<MdHistory size={24} />} 
            color="#ed6c02" 
            loading={stats.loading}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <IndicatorCard 
            title="Operação" 
            value="100%" 
            icon={<MdVerifiedUser size={24} />} 
            color="#2e7d32" 
          />
        </Grid2>
      </Grid2>

      <Typography variant="h6" fontWeight={800} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MdOutlineDashboard color={theme.palette.primary.main} /> Atalhos de Ação Rápida
      </Typography>

      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Gerenciar Prazos" 
            subtitle="Configurar entregas e fluxos" 
            icon={<MdLocalShipping size={24} />} 
            path="/hcp/configuracoes" 
            color={theme.palette.primary.main}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Equipe de Produção" 
            subtitle="Controlar acessos e permissões" 
            icon={<MdPeople size={24} />} 
            path="/hcp/colaboradores" 
            color="#1976d2"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Fluxo de Artes" 
            subtitle="Monitorar criações em andamento" 
            icon={<MdBrush size={24} />} 
            path="/hcp/arte-andamento" 
            color="#9c27b0"
            badge={stats.arteFinalCount}
            badgeColor="#9c27b0"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Montagem de Cartelas" 
            subtitle="Otimização de tiragens" 
            icon={<MdLabel size={24} />} 
            path="/hcp/montagem-cartelas" 
            color="#2e7d32"
            badge={stats.montagemCount}
            badgeColor="#2e7d32"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Fluxo de Produção" 
            subtitle="Gestão física de itens" 
            icon={<MdOutlinePrecisionManufacturing size={24} />} 
            path="/hcp/producao" 
            color="#f44336"
            badge="Em Breve"
            badgeColor="#9e9e9e"
            disabled
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <ShortcutCard 
            title="Logística HCP" 
            subtitle="Expedição e triagem" 
            icon={<MdOutlineDashboard size={24} />} 
            path="/hcp/logistica" 
            color="#ff9800"
            badge="Em Breve"
            badgeColor="#9e9e9e"
            disabled
          />
        </Grid2>
      </Grid2>

    </Box>
  )
}

export default HCPDashboard
